import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  LinearProgress,
  makeStyles,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core"
import {
  DeleteForeverSharp,
  FileCopyOutlined,
  FileCopyRounded,
  ThumbDownAlt,
  ThumbDownAltOutlined,
  ThumbUpAlt,
  ThumbUpAltOutlined,
} from "@material-ui/icons"
import { useEffect, useRef, useState } from "react"
import Markdown from "react-markdown"
import CheckMarkCircle from "../../assets/checkmarkCircle.svg"
import Coffee from "../../assets/Coffee.svg"
import Notes from "../../assets/Notes.png"
import Overview from "../../assets/Overview.png"
import SecondOpinion from "../../assets/SecondOpinion.png"
import WandMagicSparklesSolid from "../../assets/WandMagicSparklesSolid.svg"
import { ClientFragment } from "../../generated/graphql"
import { useSession } from "../../hooks/useSession"
import { Role } from "../../utils/constants"
import { colors } from "../../utils/theme"

interface AIProps {
  client: ClientFragment
  userId: string
  prompts: any[]
  text: string
  tab: string
  updateCache: any
  cache: any
}

export default function AI({
  client,
  userId,
  prompts,
  text,
  tab,
  cache,
  updateCache,
}: AIProps) {
  const { session } = useSession()
  const supervisorId =
    session?.role === Role.WEB_ROLE ? session.userId : undefined

  const [isWaiting, setIsWaiting] = useState<boolean>(false)
  const [input, setInput] = useState<string>("")
  const [messages, setMessages] = useState<Array<any>>(new Array<any>())
  const [liked, setLiked] = useState<Array<Number>>([])
  const [disliked, setDisliked] = useState<Array<Number>>([])
  const [disabled, setDisabled] = useState<Array<Number>>([])
  const [chatHistory, setChatHistory] = useState<Array<any>>(new Array<any>())
  const [display, setDisplay] = useState<boolean>(false) // controls the clear chat button and text field when conversation limit is reached
  const [copied, setCopied] = useState<Array<Number>>([])
  const markdownRef = useRef<Array<HTMLDivElement | null>>([])
  const [summary, setSummary] = useState<string>("")
  const [isGenerating, setIsGenerating] = useState<boolean>(true)
  const summaryHeader = `Summary overview on ${client.first_name} since the last appointment`
  const [showSummary, setShowSummary] = useState(true)
  const icons = new Map()
  icons.set("pre-session", [Overview, CheckMarkCircle])
  // Post-session icons keyed by prompt id so omitting Draft (id 3) when
  // vira_scribe is on does not shift Second Opinion / Self-Care icons.
  const postSessionIconsById: Record<number, string> = {
    3: Notes,
    4: SecondOpinion,
    5: Coffee,
  }
  const iconImg: any[] = icons.get(tab) ?? []
  const seeMorePrompt =
    "Please look at this client’s data since the last session and now. Highlight the most important things that have happened in terms of trends of change in behavioral or assessment scores, compliance with the treatment plan, and any abnormal or anomalous details observed."

  useEffect(() => {
    if (tab === "pre-session") {
      if (cache.length === 0) {
        const summaryPrompt = `
          Please analyze the data sources for this client since their last session. Focus on: 
          - Significant changes: Identify trends in behavioral or assessment scores, deviations from expected treatment plan compliance (e.g., missed nudges and assessments), and any abnormal or anomalous details (e.g., unexpected changes in behavioral data, unusual sleep patterns, concerning self-reported symptoms). 
          - Clinical significance: Prioritize changes that may have a significant impact on the client's health or well-being, such as those indicating a deterioration in condition, an increased risk of adverse events, or the need for immediate intervention. 
          - Contextual awareness: Consider the client's demographics, current treatment plan, history of symptom severity, and personal goals when assessing the significance of observed changes. Output: 
          - Introduction: "Here are the major developments since the last session on [Date of last session]." 
          - List 4-5 of the most significant findings in one sentence or less each. 
          - If a risk alert was triggered, report the details of the risk alert as the first finding. 
          Conclude with a brief summary of the key findings and their potential clinical implications in 2 sentences. 
          Format: Present the findings in a clear and concise format, such as a bulleted list.
          `

        const fetchSummary = async () => {
          let requestBody = {
            chat_history: [],
            chat_input: summaryPrompt,
            user_id: client.id,
            practitioner_id: userId,
            supervisor_id: supervisorId ?? null,
          }
          const response = await fetch(
            process.env.REACT_APP_KSANA_OPENAI_APIM_ENDPOINT +
              "/conversation" || "",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "ksana-openai-key":
                  process.env.REACT_APP_KSANA_OPENAI_APIM_KEY || "",
              },
              body: JSON.stringify(requestBody),
            }
          )
          const { chat_output } = await response.json()
          setSummary(chat_output)
          setIsGenerating(false)
          updateCache(chat_output)
        }
        fetchSummary()
      } else {
        setIsGenerating(false)
        setSummary(cache)
      }
    } else {
      setIsGenerating(false)
      setShowSummary(false)
    }
  }, [tab])

  const useStyles = makeStyles((theme) => ({
    ai: {
      border: `1px solid ${colors.neutral.clay}`,
      borderRadius: 4,
      padding: theme.spacing(3),
      paddingTop: 32,
      marginBottom: theme.spacing(3),
      width: "100%",
    },
    md: {
      color: "#1E263C",
      fontSize: "16px",
      fontFamily: "Lato",
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: "03.px",
    },
    gradientCard: {
      alignItems: "flex-start",
      borderRadius: "16px",
      boxShadow: "0px 3px 8px 0px rgba(66, 79, 128, 0.15)",
      border: "none",
      padding: "2px",
      background:
        "linear-gradient(95deg, #D33444 1.3%, #FFAC62 22.02%, #FDCD25 43.74%, #33C189 63.97%, #0BBBE2 82.73%, #8962D0 100%)",
      color: "#4C288E",
    },
    childContainer: {
      background: "rgba(255, 255, 255, 0.85)",
      padding: "20px",
      borderRadius: "14px",
    },
    wandContainer: {
      borderRadius: "360px",
      border: "none",
      padding: "2px",
      background:
        "linear-gradient(95deg, #D33444 1.3%, #FFAC62 22.02%, #FDCD25 43.74%, #33C189 63.97%, #0BBBE2 82.73%, #8962D0 100%), #FFFFFF",
      boxShadow: "0px 3px 8px 0px rgba(66, 79, 128, 0.15)",
    },
    wandChild: {
      background: "rgba(255, 255, 255, 0.85)",
      padding: "5px",
      borderRadius: "14px",
    },
  }))

  const createNewMessage = (content: string, isUser: boolean) => {
    const newMessage = {
      content,
      isUser,
    }
    return newMessage
  }

  const handleSendMessage = async (input: string) => {
    const presetPrompt = prompts.find((e) => e.id === Number(input))
    const msg =
      typeof presetPrompt !== "undefined"
        ? createNewMessage(presetPrompt.heading, true)
        : createNewMessage(
            input === "see more"
              ? "What has happened since the last appointment?"
              : input,
            true
          )
    const chatInput =
      typeof presetPrompt !== "undefined"
        ? presetPrompt.prompt
        : input === "see more"
        ? seeMorePrompt
        : input
    setMessages([...messages, msg])
    setIsWaiting(true)
    setShowSummary(false)

    // Limit the length of chat conversation
    if (
      chatHistory.length ===
      parseInt(process.env.REACT_APP_KSANA_OPENAI_CHAT_HISTORY_LIMIT || "5")
    ) {
      setMessages([
        ...messages,
        createNewMessage(
          "You have reached the limit. Please start a new conversation.",
          false
        ),
      ])
      setInput("")
      setIsWaiting(false)
      setDisplay(true)
      return
    }

    let requestBody = {
      chat_history: chatHistory,
      chat_input: chatInput,
      user_id: client.id,
      practitioner_id: userId,
      supervisor_id: supervisorId ?? null,
    }

    const response = await fetch(
      process.env.REACT_APP_KSANA_OPENAI_APIM_ENDPOINT + "/conversation" || "",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ksana-openai-key": process.env.REACT_APP_KSANA_OPENAI_APIM_KEY || "",
        },
        body: JSON.stringify(requestBody),
      }
    )

    if (!response.ok) {
      setMessages([
        createNewMessage(
          "Invalid query or topic. Please try another query or topic.",
          false
        ),
        ...messages,
      ])
      setIsWaiting(false)
      return
    }
    const { chat_output } = await response.json()
    const entry = {
      inputs: {
        chat_input: chatInput,
      },
      outputs: {
        chat_output,
      },
    }
    setChatHistory([...chatHistory, entry])
    setMessages([...messages, msg, createNewMessage(chat_output, false)])
    setInput("")
    setIsWaiting(false)
  }

  // detect enter key and send message
  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      handleSendMessage(input)
    }
  }

  const clearChatHandler = async () => {
    setMessages([])
    setDisliked([])
    setLiked([])
    setDisabled([])
    setChatHistory([])
    setDisplay(false)
    setCopied([])
    setIsGenerating(false)
    setShowSummary(tab === "pre-session" ? true : false)
  }

  const feedbackHandler = async (flag: string, index: number) => {
    setDisabled([...disabled, index])
    if (flag === "disliked") {
      let result = [...disliked, index]
      setDisliked(result)
    } else if (flag === "liked") {
      let result = [...liked, index]
      setLiked(result)
    }

    const requestBody = {
      client_id: client.id,
      practitioner_id: userId,
      prompt: messages[index - 1]["content"],
      model_output: messages[index]["content"],
      score: flag === "liked" ? 1 : 0,
      supervisor_id: supervisorId ?? null,
      chatHistory,
    }

    await fetch(
      process.env.REACT_APP_KSANA_OPENAI_APIM_ENDPOINT + "/feedback" || "",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ksana-openai-key": process.env.REACT_APP_KSANA_OPENAI_APIM_KEY || "",
        },
        body: JSON.stringify(requestBody),
      }
    )
    return
  }

  /*
      Helper function to copy the prompt response
    */
  const handleCopy = async (index: number) => {
    const div = markdownRef.current[index]
    if (div !== null && div !== undefined) {
      const range = document.createRange()
      range.selectNodeContents(div)
      const selection = window.getSelection()
      if (selection !== null) {
        selection.removeAllRanges()
        selection.addRange(range)
        await navigator.clipboard.writeText(selection?.toString())
        selection.removeAllRanges()
      }
      setCopied([...copied, index])
    }
  }

  const showClearChat = messages.length > 1

  const classes = useStyles()
  return (
    <div className={classes.ai}>
      {/* clear chat */}
      <Grid container direction="row">
        {messages.length > 0 && (
          <Button
            startIcon={<DeleteForeverSharp />}
            style={{
              textTransform: "none",
              borderRadius: "5px",
              height: "30px",
            }}
            variant="outlined"
            onClick={() => clearChatHandler()}
          >
            <Typography variant="body2">Clear chat</Typography>
          </Button>
        )}
      </Grid>

      {/* Title */}
      <Grid
        container
        direction="row"
        justifyContent="center"
        style={{ paddingBottom: "12px", gap: "4px" }}
      >
        {messages.length === 0 && (
          <>
            <Grid item className={classes.wandContainer}>
              <Grid item className={classes.wandChild}>
                <img src={WandMagicSparklesSolid} width={24} height={20} />
              </Grid>
            </Grid>
            <Grid item>
              <Typography
                variant="h3"
                style={{
                  fontSize: "24px",
                  fontStyle: "normal",
                  fontWeight: "700",
                  lineHeight: "28px",
                  color: "#4C288E",
                  paddingTop: "5px",
                }}
              >
                {text}
              </Typography>
            </Grid>
          </>
        )}
      </Grid>

      {showSummary && (
        <Grid
          container
          direction="row"
          className={isGenerating ? "" : classes.gradientCard}
        >
          {isGenerating ? (
            <>
              <Grid item style={{ display: "flex" }}>
                <Typography variant="body2">Generating summary</Typography>
                <img
                  src={WandMagicSparklesSolid}
                  style={{
                    height: "20px",
                    float: "left",
                    padding: "1.25px 5px 0.972px 0px",
                  }}
                />
              </Grid>
              <LinearProgress style={{ width: "100%" }} />
            </>
          ) : (
            <>
              <Grid item className={classes.childContainer}>
                <div>
                  <img
                    src={WandMagicSparklesSolid}
                    style={{
                      height: "20px",
                      float: "left",
                      padding: "1.25px 5px 0.972px 0px",
                    }}
                  />
                  <p
                    style={{
                      lineHeight: "20px",
                      fontWeight: "700",
                      fontSize: "20px",
                    }}
                  >
                    {summaryHeader}
                  </p>
                </div>
                <div
                  style={{
                    fontFamily: "Lato",
                    fontSize: "17px",
                    fontStyle: "normal",
                    fontWeight: "500",
                    lineHeight: "25px",
                  }}
                >
                  <Markdown>{summary}</Markdown>
                </div>
                <Button
                  style={{
                    float: "right",
                    borderRadius: "200px",
                    border: "2px solid rgba(79, 102, 170, 1)",
                    background: "rgba(255, 255, 255, 0.85)",
                    height: "32px",
                  }}
                  onClick={() => handleSendMessage("see more")}
                >
                  <p
                    style={{
                      color: "rgba(79, 102, 170, 1)",
                      fontSize: "14px",
                      fontFamily: "Lato",
                      fontStyle: "normal",
                      fontWeight: "700",
                      lineHeight: "16px",
                      textTransform: "none",
                    }}
                  >
                    See More
                  </p>
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      )}

      {/* predefined prompts */}
      <Grid
        container
        direction="row"
        justifyContent="center"
        style={{ rowGap: "1em", paddingTop: "25px" }}
      >
        {messages.length === 0 && (
          <>
            {prompts.map((prompt, index) => (
              <Button
                style={{
                  textTransform: "none",
                  borderRadius: "10px",
                  alignContent: "center",
                  background: "rgb(231, 231, 231)",
                  height: "100px",
                  border: "1px solid rgba(0, 0, 0, 0.1)",
                  marginRight: "10px",
                  width: "300px",
                }}
                onClick={() => handleSendMessage(`${prompt.id}`)}
              >
                <img
                  src={
                    tab === "post-session"
                      ? postSessionIconsById[prompt.id] ?? iconImg[index]
                      : iconImg[index]
                  }
                  style={{
                    height: "35px",
                    paddingRight: "5px",
                  }}
                />
                <div
                  style={{
                    fontFamily: "Lato",
                    fontSize: "16px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "16px",
                    textAlign: "start",
                  }}
                >
                  {prompt.heading}
                </div>
              </Button>
            ))}
          </>
        )}
      </Grid>

      {/* chat */}
      <Grid
        container
        direction="column"
        spacing={2}
        style={{ marginTop: "50px" }}
      >
        {messages.map((message, index) => (
          <>
            <Grid
              item
              style={{
                display: "flex",
                justifyContent: message.isUser ? "flex-end" : "flex-start",
              }}
              key={index}
            >
              {message.isUser && message.content.length > 0 ? (
                <Card
                  style={{
                    backgroundColor: "#EEF5FD",
                    borderRadius: "10px",
                  }}
                >
                  <CardContent>
                    <Typography variant="body1">{message.content}</Typography>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Card style={{ borderRadius: "10px" }}>
                    <Box
                      style={{
                        display: "flex",
                      }}
                      paddingBottom={0.5}
                      justifyContent="flex-end"
                    >
                      <Tooltip
                        title={copied.includes(index) ? "Copied!" : "Copy"}
                      >
                        <IconButton onClick={() => handleCopy(index)}>
                          {copied.includes(index) ? (
                            <FileCopyRounded style={{ fontSize: "23px" }} />
                          ) : (
                            <FileCopyOutlined style={{ fontSize: "23px" }} />
                          )}
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        onClick={() => feedbackHandler("disliked", index)}
                        disabled={disabled.includes(index)}
                        style={{ padding: "4px" }}
                      >
                        {disliked.includes(index) ? (
                          <ThumbDownAlt style={{ fontSize: "23px" }} />
                        ) : (
                          <ThumbDownAltOutlined style={{ fontSize: "23px" }} />
                        )}
                      </IconButton>
                      <IconButton
                        onClick={() => feedbackHandler("liked", index)}
                        disabled={disabled.includes(index)}
                      >
                        {liked.includes(index) ? (
                          <ThumbUpAlt style={{ fontSize: "23px" }} />
                        ) : (
                          <ThumbUpAltOutlined style={{ fontSize: "23px" }} />
                        )}
                      </IconButton>
                    </Box>
                    <CardContent className={classes.md}>
                      <div
                        ref={(element) => {
                          markdownRef.current[index] = element
                        }}
                      >
                        <Markdown>{message.content}</Markdown>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </Grid>
            {!message.isUser && message.content.length > 0 && (
              <div style={{ paddingTop: "8px", paddingLeft: "10px" }}>
                <Typography style={{ fontStyle: "italic" }}>
                  AI can make errors – Make sure to check all responses and use
                  your professional judgement.
                </Typography>
              </div>
            )}
          </>
        ))}
        {isWaiting && (
          <>
            <Grid item style={{ display: "flex" }}>
              <Typography variant="body2">Generating answer</Typography>
              <img
                src={WandMagicSparklesSolid}
                style={{
                  height: "20px",
                  float: "left",
                  padding: "1.25px 5px 0.972px 0px",
                }}
              />
            </Grid>
            <LinearProgress />
          </>
        )}
      </Grid>

      {/* clear chat */}
      {showClearChat && (
        <Grid container direction="row" style={{ paddingTop: "30px" }}>
          <Button
            startIcon={<DeleteForeverSharp />}
            style={{
              textTransform: "none",
              borderRadius: "5px",
              height: "30px",
            }}
            variant="outlined"
            onClick={() => clearChatHandler()}
          >
            <Typography variant="body2">Clear chat</Typography>
          </Button>
        </Grid>
      )}

      {/* chat input box */}
      {!display && (
        <Grid
          container
          direction="row"
          style={{ marginTop: "30px" }}
          justifyContent="center"
        >
          {messages.length === 0 && (
            <Typography style={{ paddingBottom: "12px" }}>
              Or Ask Vira AI Anything About This Case.
            </Typography>
          )}
          <TextField
            id="outlined-basic"
            variant="outlined"
            style={{ width: "100%" }}
            multiline
            minRows={4}
            maxRows={10}
            placeholder="Type query here. (Shift + Enter) for new line"
            disabled={isWaiting}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
          />
        </Grid>
      )}
    </div>
  )
}
